import { Button, ButtonMenu, Table, TableColumn, TextField } from '@scale/scaleui';
import { SquadMemberRole, SquadType } from '@scale/shared/types/expertManagement';
import { useCallback, useMemo, useState } from 'react';
import { AddWorkersToSquadModal } from 'react-scale/components/ExpertManagement/CommunityManagement/AddWorkersToSquadModal';
import { DeleteWorkersConfirmationModal } from 'react-scale/components/ExpertManagement/CommunityManagement/DeleteWorkersConfirmationModal';
import { LoadingTable } from 'react-scale/components/ExpertManagement/ExpertManagementPage/LoadingTable';
import { useTableColumnDisplayOrder } from 'react-scale/corp/components/UI/TableColumnDisplayOrder/useTableColumnDisplayOrder';

const SQUAD_ROLE_TO_NAME_MAP: Record<SquadMemberRole, string> = {
  [SquadMemberRole.TeamLeadCoordinator]: 'Team Lead Coordinator',
  [SquadMemberRole.TeamLead]: 'Team Lead',
  [SquadMemberRole.Contributor]: 'Contributor',
};

interface SquadMemberTableProps {
  data: object[];
  isLoading: boolean;
  refetch: () => Promise<any>;
  squadId: string;
  squadType: SquadType;
}

export const SquadMemberTable: React.FC<SquadMemberTableProps> = ({
  data,
  isLoading,
  refetch,
  squadId,
  squadType,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [rowSelectionState, setRowSelectionState] = useState<{ [key: string]: boolean }>({});
  const [showAddWorkersToSquadModal, setShowAddWorkersToSquadModal] = useState(false);
  const [showDeleteWorkersConfirmationModal, setShowDeleteWorkersConfirmationModal] =
    useState(false);
  const [selectedRole, setSelectedRole] = useState<
    SquadMemberRole.Contributor | SquadMemberRole.TeamLead
  >(SquadMemberRole.Contributor);

  const columns: TableColumn[] = [
    {
      id: 'name',
      header: 'Name',
      cell: function ExpertName(props: any) {
        const { name, id } = props.row.original;
        if (!name) {
          return null;
        }
        return (
          <div className="flex flex-col">
            <div>{name}</div>
            <div className="text-xs">{id}</div>
          </div>
        );
      },
    },
    {
      id: 'email',
      header: 'Email',
    },
    {
      id: 'role',
      header: 'Role',
      cell: function ManagerRole(props: any) {
        const role = props.getValue();
        return SQUAD_ROLE_TO_NAME_MAP[role as SquadMemberRole] ?? '-';
      },
    },
  ];

  const displayState = useTableColumnDisplayOrder('expert-table', columns);

  const filteredData = useMemo(() => {
    if (!searchValue) {
      return data;
    }
    return data.filter((d: any) =>
      d.name
        .toLowerCase()
        .includes(
          searchValue.toLowerCase() ||
            d.email.toLowerCase().includes(searchValue.toLowerCase()) ||
            d.id.includes(searchValue),
        ),
    );
  }, [data, searchValue]);

  const selectedRows = useMemo(() => {
    if (!filteredData) {
      return [];
    }
    return filteredData.filter((_row: any, index: number) => rowSelectionState[index]);
  }, [filteredData, rowSelectionState]);

  const showAddWorkersModal = useCallback(
    (role: SquadMemberRole.Contributor | SquadMemberRole.TeamLead) => {
      setSelectedRole(role);
      setShowAddWorkersToSquadModal(true);
    },
    [setSelectedRole, setShowAddWorkersToSquadModal],
  );

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between">
          <TextField
            className="w-[300px]"
            placeholder="Search by name, email, or ID.."
            value={searchValue}
            onChange={t => setSearchValue(t.target.value)}
            trailingIcon={'xmark'}
            onTrailingIconClick={() => setSearchValue('')}
          />
          <div className="flex gap-2">
            <ButtonMenu
              menuItems={[
                {
                  label: 'Add to Squad',
                },
                {
                  label: 'Add CBs to Squad',
                  disabled: !squadType || squadType === SquadType.TeamLeadCoordinator,
                  onClick: () => showAddWorkersModal(SquadMemberRole.Contributor),
                },
                {
                  label: 'Add TLs to Squad',
                  disabled:
                    !squadType ||
                    ![SquadType.TeamLeadCoordinator, SquadType.Community].includes(squadType),
                  onClick: () => showAddWorkersModal(SquadMemberRole.TeamLead),
                },
              ]}
            />
            <Button
              size="lg"
              disabled={selectedRows.length === 0}
              onClick={() => setShowDeleteWorkersConfirmationModal(true)}
              variant="danger"
            >
              Remove from squad
            </Button>
          </div>
        </div>
        {isLoading ? (
          <LoadingTable columns={displayState.displayColumns} numRows={3} rowHeight={2} />
        ) : (
          <Table
            columns={displayState.displayColumns}
            data={filteredData}
            rowSelectionState={rowSelectionState}
            onRowSelectionStateChange={s => setRowSelectionState(s)}
            enableRowSelection
            defaultPageSize={10}
            pageSizeOptions={[]}
          />
        )}
      </div>
      <AddWorkersToSquadModal
        onClose={() => setShowAddWorkersToSquadModal(false)}
        open={showAddWorkersToSquadModal}
        refetch={refetch}
        squadId={squadId}
        role={selectedRole}
        squadType={squadType}
      />
      <DeleteWorkersConfirmationModal
        onClose={() => setShowDeleteWorkersConfirmationModal(false)}
        open={showDeleteWorkersConfirmationModal}
        refetch={refetch}
        squadId={squadId}
        selectedRows={selectedRows}
      />
    </>
  );
};
